import React, { useState } from "react";
import { Rnd } from "react-rnd";
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";

const DraggableFloorplan = ({ floorPlanImages = [], onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? floorPlanImages.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === floorPlanImages.length - 1 ? 0 : prev + 1));
  };

  return (
    <Rnd
      default={{
        x: window.innerWidth - 520,
        y: 100,
        width: 500,
        height: 400,
      }}
      minWidth={300}
      minHeight={200}
      lockAspectRatio={true}
      className="z-50 bg-white/90 backdrop-blur-sm shadow-2xl rounded-xl border border-gray-200 overflow-hidden"
      dragHandleClassName="drag-handle"
    >
      {/* Header / Drag Handle */}
      <div className="drag-handle h-[40px] flex items-center justify-between px-3 bg-gray-50/80 border-b border-gray-200 cursor-move select-none">
        <div className="flex items-baseline gap-2">
          <h3 className="text-sm font-semibold text-gray-700">Floor Plan</h3>
          <p className="text-[10px] text-gray-500 uppercase tracking-wider">
            {floorPlanImages.length > 0 ? `Floor ${currentIndex + 1} of ${floorPlanImages.length}` : "No floor plan available"}
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-200 rounded-full transition-colors"
        >
          <XMarkIcon className="w-5 h-5 text-gray-500" />
        </button>
      </div>

      {/* Content */}
      <div className="absolute top-[40px] bottom-0 left-0 right-0 bg-white overflow-hidden group">
        {floorPlanImages.length > 0 ? (
          <div className="w-full h-full flex items-center justify-center relative">
            <img
              src={floorPlanImages[currentIndex]}
              alt={`Floor Plan ${currentIndex + 1}`}
              className="w-full h-full object-contain pointer-events-none select-none"
            />

            {/* Navigation Arrows & Dots - Visible on Hover */}
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              {/* Navigation Arrows */}
              {floorPlanImages.length > 1 && (
                <>
                  <button
                    onClick={handlePrev}
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white text-gray-800 shadow-md border border-gray-100 transition-all z-20 backdrop-blur-sm"
                  >
                    <ChevronLeftIcon className="w-6 h-6" />
                  </button>
                  <button
                    onClick={handleNext}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 hover:bg-white text-gray-800 shadow-md border border-gray-100 transition-all z-20 backdrop-blur-sm"
                  >
                    <ChevronRightIcon className="w-6 h-6" />
                  </button>
                </>
              )}

              {/* Dots Indicator */}
              {floorPlanImages.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full border border-gray-200 shadow-sm">
                  {floorPlanImages.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentIndex(idx)}
                      className={`w-2.5 h-2.5 rounded-full transition-all ${
                        currentIndex === idx
                          ? "bg-gray-800 scale-110"
                          : "bg-gray-300 hover:bg-gray-500"
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400 text-xs">
            No floor plan available
          </div>
        )}
      </div>
    </Rnd>
  );
};

export default DraggableFloorplan;
